/* ============================================================
   data.ts — ALL the game's "content" lives here.

   This file is on purpose separate from the game logic. To add a
   new trash item or a new level later, you only edit THIS file —
   you don't have to touch the game engine. See README.md.
   ============================================================ */

export type BinId = "compost" | "recyclable" | "hazardous" | "nonRecyclable";

export interface Bin {
  /** internal name used by items below ("bin" field) */
  id: BinId;
  /** what the player sees */
  label: string;
  /** bin color from the design doc */
  color: string;
  /** little icon shown on the bin */
  emoji: string;
}

export interface WasteItem {
  /** shown to the player */
  name: string;
  /** the picture (swap for real images later if you want) */
  emoji: string;
  /** which bin id is CORRECT for this item */
  bin: BinId;
  /** a short educational fact shown after sorting */
  fact: string;
}

export interface Level {
  level: number;
  /** pixels per second the item falls */
  speed: number;
  /** how many correct sorts finish the level */
  itemsToSort: number;
  /** how many items can fall at the same time */
  concurrent: number;
  /** which bins appear on screen this level */
  allowedBins: BinId[];
  /** the learning theme (shown at level start) */
  focus: string;
}

/* ---- The four bins ---------------------------------------- */
export const BINS: Bin[] = [
  { id: "compost", label: "Compost", color: "#8B5E3C", emoji: "🌱" },
  { id: "recyclable", label: "Recyclable", color: "#206135", emoji: "♻️" },
  { id: "hazardous", label: "Hazardous", color: "#D97706", emoji: "⚠️" },
  { id: "nonRecyclable", label: "Non-Recyclable", color: "#4B5563", emoji: "🗑️" },
];

// Quick lookup helper: BIN_BY_ID["compost"] -> the compost bin object
export const BIN_BY_ID = Object.fromEntries(BINS.map((b) => [b.id, b])) as Record<BinId, Bin>;

/* ---- The waste items --------------------------------------- */
export const ITEMS: WasteItem[] = [
  // --- Compost ---
  {
    name: "Banana Peel",
    emoji: "🍌",
    bin: "compost",
    fact: "Banana peels break down in weeks and enrich soil with potassium.",
  },
  {
    name: "Apple Core",
    emoji: "🍎",
    bin: "compost",
    fact: "Apple cores compost quickly — seeds and all.",
  },
  {
    name: "Vegetable Scraps",
    emoji: "🥦",
    bin: "compost",
    fact: "Veggie scraps turn into nutrient-rich compost, not landfill methane.",
  },
  {
    name: "Coffee Grounds",
    emoji: "☕",
    bin: "compost",
    fact: "Used coffee grounds add nitrogen that composting microbes love.",
  },
  {
    name: "Tea Bags",
    emoji: "🫖",
    bin: "compost",
    fact: "Most tea leaves compost well — just check the bag isn't plastic.",
  },
  {
    name: "Eggshells",
    emoji: "🥚",
    bin: "compost",
    fact: "Crushed eggshells add calcium to compost.",
  },
  {
    name: "Dry Leaves",
    emoji: "🍂",
    bin: "compost",
    fact: "Dry leaves are 'browns' — the carbon that balances a compost pile.",
  },
  {
    name: "Grass Clippings",
    emoji: "🌿",
    bin: "compost",
    fact: "Fresh grass clippings are 'greens', rich in nitrogen.",
  },
  {
    name: "Fruit Peels",
    emoji: "🍊",
    bin: "compost",
    fact: "Fruit peels compost down and keep food waste out of landfill.",
  },
  {
    name: "Wilted Flowers",
    emoji: "🥀",
    bin: "compost",
    fact: "Old flowers and plant trimmings belong in the compost.",
  },

  // --- Recyclable ---
  {
    name: "PET Bottle",
    emoji: "🧴",
    bin: "recyclable",
    fact: "Empty, rinsed plastic bottles can become new bottles or clothing fibre.",
  },
  {
    name: "Glass Jar",
    emoji: "🫙",
    bin: "recyclable",
    fact: "Glass is endlessly recyclable without losing quality.",
  },
  {
    name: "Aluminium Can",
    emoji: "🥫",
    bin: "recyclable",
    fact: "Recycling a can saves ~95% of the energy of making a new one.",
  },
  {
    name: "Cardboard",
    emoji: "📦",
    bin: "recyclable",
    fact: "Flatten clean, dry cardboard so it recycles efficiently.",
  },
  {
    name: "Newspaper",
    emoji: "📰",
    bin: "recyclable",
    fact: "Paper can be recycled 5–7 times before fibres get too short.",
  },

  // --- Hazardous ---
  {
    name: "Battery",
    emoji: "🔋",
    bin: "hazardous",
    fact: "Batteries leak toxic metals — take them to a hazardous drop-off.",
  },
  {
    name: "Smartphone",
    emoji: "📱",
    bin: "hazardous",
    fact: "Old phones are e-waste; recycle them to recover precious metals.",
  },
  {
    name: "Paint Can",
    emoji: "🎨",
    bin: "hazardous",
    fact: "Leftover paint is hazardous — never pour it down the drain.",
  },
  {
    name: "Medicine",
    emoji: "💊",
    bin: "hazardous",
    fact: "Return unused medicine to a pharmacy take-back, not the bin.",
  },
  {
    name: "Light Bulb",
    emoji: "💡",
    bin: "hazardous",
    fact: "Some bulbs contain mercury and need special disposal.",
  },

  // --- Non-Recyclable ---
  {
    name: "Pizza Box",
    emoji: "🍕",
    bin: "nonRecyclable",
    fact: "Greasy pizza boxes contaminate recycling — they go to general waste.",
  },
  {
    name: "Face Mask",
    emoji: "😷",
    bin: "nonRecyclable",
    fact: "Disposable masks mix materials and can't be recycled.",
  },
  {
    name: "Ceramic",
    emoji: "🏺",
    bin: "nonRecyclable",
    fact: "Ceramics melt at a different temperature than glass — not recyclable.",
  },
  {
    name: "Plastic Film",
    emoji: "🎞️",
    bin: "nonRecyclable",
    fact: "Thin plastic film jams recycling machines; keep it out.",
  },
  {
    name: "Styrofoam",
    emoji: "🧊",
    bin: "nonRecyclable",
    fact: "Styrofoam is rarely recyclable and breaks into harmful bits.",
  },
];

// Group items by bin — handy for the "Learn About Waste" screen and
// for building each level's item pool.
export const ITEMS_BY_BIN = BINS.reduce(
  (acc, bin) => {
    acc[bin.id] = ITEMS.filter((it) => it.bin === bin.id);
    return acc;
  },
  {} as Record<BinId, WasteItem[]>,
);

/* ---- Levels -------------------------------------------------
   All 15 levels from the design doc. To tweak one, just edit the
   numbers; to add more, copy a line (see README.md).
------------------------------------------------------------- */
const ALL_BINS: BinId[] = ["compost", "recyclable", "hazardous", "nonRecyclable"];
const BASIC_BINS: BinId[] = ["compost", "recyclable", "nonRecyclable"];

export const LEVELS: Level[] = [
  {
    level: 1,
    speed: 70,
    itemsToSort: 8,
    concurrent: 1,
    allowedBins: ["recyclable", "nonRecyclable"],
    focus: "Recyclable vs Non-Recyclable",
  },
  {
    level: 2,
    speed: 85,
    itemsToSort: 10,
    concurrent: 1,
    allowedBins: ["recyclable", "nonRecyclable"],
    focus: "More common household waste",
  },
  {
    level: 3,
    speed: 100,
    itemsToSort: 12,
    concurrent: 1,
    allowedBins: BASIC_BINS,
    focus: "Introducing Compost",
  },
  {
    level: 4,
    speed: 115,
    itemsToSort: 15,
    concurrent: 1,
    allowedBins: BASIC_BINS,
    focus: "Mixed sorting across 3 bins",
  },
  {
    level: 5,
    speed: 130,
    itemsToSort: 18,
    concurrent: 1,
    allowedBins: BASIC_BINS,
    focus: "All basic waste types",
  },
  {
    level: 6,
    speed: 140,
    itemsToSort: 20,
    concurrent: 1,
    allowedBins: BASIC_BINS,
    focus: "Clean vs contaminated recyclables",
  },
  {
    level: 7,
    speed: 150,
    itemsToSort: 25,
    concurrent: 1,
    allowedBins: BASIC_BINS,
    focus: "More compostable materials",
  },
  {
    level: 8,
    speed: 160,
    itemsToSort: 30,
    concurrent: 1,
    allowedBins: ALL_BINS,
    focus: "⚠️ Introducing Hazardous Waste",
  },
  {
    level: 9,
    speed: 175,
    itemsToSort: 35,
    concurrent: 1,
    allowedBins: ALL_BINS,
    focus: "Batteries, electronics & medicines",
  },
  {
    level: 10,
    speed: 190,
    itemsToSort: 40,
    concurrent: 1,
    allowedBins: ALL_BINS,
    focus: "Tricky look-alike items",
  },
  {
    level: 11,
    speed: 200,
    itemsToSort: 45,
    concurrent: 2,
    allowedBins: ALL_BINS,
    focus: "Multiple items at once!",
  },
  {
    level: 12,
    speed: 215,
    itemsToSort: 50,
    concurrent: 2,
    allowedBins: ALL_BINS,
    focus: "Contamination awareness challenge",
  },
  {
    level: 13,
    speed: 230,
    itemsToSort: 60,
    concurrent: 2,
    allowedBins: ALL_BINS,
    focus: "Advanced mixed waste",
  },
  {
    level: 14,
    speed: 245,
    itemsToSort: 70,
    concurrent: 3,
    allowedBins: ALL_BINS,
    focus: "Minimal reaction time!",
  },
  {
    level: 15,
    speed: 260,
    itemsToSort: 80,
    concurrent: 3,
    allowedBins: ALL_BINS,
    focus: "🌍 Final Sustainability Challenge",
  },
];

/* ---- Scoring (straight from the design doc) ---------------- */
export const SCORING = {
  correct: 100,
  fastBonus: 50, // EXTRA when sorted in under 2 seconds (100 -> 150)
  fastThresholdMs: 2000,
  compostCorrect: 125, // flat points for a correct compost sort
  hazardousCorrect: 150, // flat points for a correct hazardous sort
  wrongBin: -50,
  missed: -75,
  streak5Bonus: 250,
  streak10Bonus: 500,
  perfectLevelBonus: 1000,
} as const;

/* ---- A few rotating sustainability quotes for the splash --- */
export const QUOTES = [
  "We do not inherit the Earth from our ancestors; we borrow it from our children.",
  "The greatest threat to our planet is the belief that someone else will save it.",
  "Every piece of trash sorted right is a small win for the planet.",
  "Reduce, reuse, recycle — in that order.",
];
